import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ChevronRight, Clock, Sparkles, Phone } from "lucide-react";
import { BRAND } from "@/config/brand";
import { CLUSTERS, getArticleBySlug, getRelatedArticles } from "@/config/articles";
import PlugitAIChat from "@/pages/PlugitAIChat";

const C = BRAND.colors;

function upsertMeta(name, content, isProperty = false) {
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function upsertCanonical(href) {
  let el = document.querySelector("link[rel='canonical']");
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
  }
  el.href = href;
}
function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export default function ArticlePage() {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);
  const cluster = article && CLUSTERS.find((c) => c.id === article.cluster);
  const related = getRelatedArticles(article);

  useEffect(() => {
    if (!article) return;
    const origin = process.env.REACT_APP_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "https://plugitai.com");
    const url = `${origin}/learn/${article.slug}`;
    document.title = article.metaTitle;
    upsertMeta("description", article.metaDescription);
    upsertMeta("robots", "index,follow,max-image-preview:large");
    upsertMeta("og:title", article.metaTitle, true);
    upsertMeta("og:description", article.metaDescription, true);
    upsertMeta("og:type", "article", true);
    upsertMeta("og:url", url, true);
    upsertMeta("og:image", `${origin}/hero-ai-twin.jpg`, true);
    upsertMeta("twitter:card", "summary_large_image");
    upsertCanonical(url);

    upsertJsonLd("ld-article", {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.metaDescription,
      author: { "@type": "Organization", name: "PlugitAI" },
      publisher: {
        "@type": "Organization",
        name: "PlugitAI",
        logo: { "@type": "ImageObject", url: `${origin}/plugitai-logo.svg` },
      },
      datePublished: article.published,
      dateModified: article.published,
      mainEntityOfPage: url,
    });

    if (article.faq?.length) {
      upsertJsonLd("ld-faq", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: article.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }
    upsertJsonLd("ld-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin },
        { "@type": "ListItem", position: 2, name: "Learn", item: `${origin}/learn` },
        { "@type": "ListItem", position: 3, name: article.title, item: url },
      ],
    });

    return () => ["ld-article", "ld-faq", "ld-breadcrumb"].forEach((id) => document.getElementById(id)?.remove());
  }, [article]);

  if (!article) return <Navigate to="/learn" replace />;

  return (
    <div className="min-h-screen w-full"
         style={{ background: C.ink, color: C.text, fontFamily: "'Inter', system-ui, sans-serif" }}
         data-testid={`article-page-${article.slug}`}>
      <TopNav />
      <article className="relative z-10 pt-28 md:pt-36 pb-24">
        <div className="mx-auto max-w-[820px] px-6 md:px-10">
          {/* Breadcrumbs */}
          <nav className="text-xs uppercase tracking-[0.18em] flex items-center gap-2 flex-wrap mb-8" style={{ color: C.textFade }}>
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight size={12} />
            <Link to="/learn" className="hover:text-white">Learn</Link>
            <ChevronRight size={12} />
            <span style={{ color: C.text }}>{article.title}</span>
          </nav>

          {/* Cluster tag */}
          {cluster && (
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.22em] mb-4"
                 style={{ background: `${cluster.accent}18`, color: cluster.accent, border: `1px solid ${cluster.accent}55` }}>
              {cluster.label}
            </div>
          )}

          {/* Title + excerpt */}
          <h1 className="font-black tracking-[-0.02em] leading-[1.04]"
              style={{ fontSize: "clamp(2rem, 4vw, 3.4rem)", color: "white" }}
              data-testid="article-h1">
            {article.h1}
          </h1>
          <p className="mt-5 text-lg md:text-xl leading-relaxed" style={{ color: C.textDim }}>
            {article.excerpt}
          </p>
          <div className="mt-4 flex items-center gap-4 text-xs uppercase tracking-[0.18em]" style={{ color: C.textFade }}>
            <span className="flex items-center gap-1.5"><Clock size={12} /> {article.readMinutes} min read</span>
            <span>·</span>
            <time dateTime={article.published}>{new Date(article.published).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</time>
          </div>

          {/* Body */}
          <div className="mt-12 space-y-10">
            {article.body.map((section, i) => (
              <div key={i}>
                <h2 className="font-black tracking-[-0.01em] mb-3" style={{ fontSize: "clamp(1.4rem, 2.2vw, 1.8rem)", color: "white" }}>
                  {section.h}
                </h2>
                <p className="text-base md:text-lg leading-relaxed" style={{ color: C.textDim }}>{section.p}</p>
              </div>
            ))}
          </div>

          {/* Inline CTA */}
          <div className="mt-16 p-8 rounded-3xl border relative overflow-hidden"
               style={{
                 background: `linear-gradient(135deg, ${C.blueBright}18 0%, ${C.greenBright}12 100%), ${C.inkSoft}`,
                 borderColor: `${C.blueBright}55`,
                 boxShadow: `0 40px 120px -40px ${C.blueBright}55`,
               }}
               data-testid="article-inline-cta">
            <h3 className="font-black leading-tight mb-3" style={{ fontSize: "clamp(1.4rem, 2.4vw, 2rem)", color: "white" }}>
              Ready to see this working in your business?
            </h3>
            <p className="text-base leading-relaxed mb-6" style={{ color: C.textDim }}>
              Lock in your project with $299.99 and let us begin designing the AI twin around your operation. Most builds are live in days, not weeks.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="tel:+12148155966"
                 className="inline-flex items-center gap-2 px-6 h-12 rounded-full font-semibold text-sm"
                 style={{
                   background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`,
                   color: "white",
                   boxShadow: `0 20px 60px -20px ${C.greenBright}88`,
                 }}
                 data-testid="article-cta-call">
                <Phone size={14} /> Call (214) 815-5966
              </a>
              <button type="button"
                      onClick={() => document.querySelector("[data-testid=plugitai-chat-open]")?.click()}
                      className="inline-flex items-center gap-2 px-6 h-12 rounded-full font-semibold text-sm border transition-all hover:bg-white/5"
                      style={{ borderColor: `${C.blueBright}66`, color: "white" }}
                      data-testid="article-cta-chat">
                Chat with Kit <ArrowRight size={14} />
              </button>
            </div>
          </div>

          {/* FAQ */}
          {article.faq?.length > 0 && (
            <div className="mt-16" data-testid="article-faq">
              <h2 className="font-black tracking-[-0.01em] mb-6" style={{ fontSize: "clamp(1.5rem, 2.4vw, 2rem)", color: "white" }}>
                Common questions
              </h2>
              <div className="space-y-3">
                {article.faq.map((f, i) => (
                  <details key={i} className="group rounded-2xl p-5 border"
                           style={{ borderColor: C.line, background: "rgba(17,24,39,0.55)", backdropFilter: "blur(10px)" }}>
                    <summary className="flex items-center justify-between gap-3 cursor-pointer text-base font-bold text-white list-none">
                      <span>{f.q}</span>
                      <ChevronRight size={16} className="shrink-0 transition-transform group-open:rotate-90" style={{ color: C.blueGlow }} />
                    </summary>
                    <p className="mt-3 text-sm leading-relaxed" style={{ color: C.textDim }}>{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          )}

          {/* Related */}
          {related.length > 0 && (
            <div className="mt-20" data-testid="article-related">
              <div className="text-xs uppercase tracking-[0.22em] mb-4" style={{ color: C.blueGlow }}>Keep reading</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {related.map((r) => (
                  <Link key={r.slug} to={`/learn/${r.slug}`}
                        className="group rounded-2xl p-5 border transition-all hover:-translate-y-1"
                        style={{ borderColor: C.line, background: "rgba(17,24,39,0.55)", backdropFilter: "blur(10px)" }}>
                    <div className="text-[9px] uppercase tracking-[0.24em] mb-2" style={{ color: C.greenGlow }}>
                      {CLUSTERS.find((c) => c.id === r.cluster)?.label || r.cluster}
                    </div>
                    <div className="font-bold text-white text-base leading-tight">{r.title}</div>
                    <div className="mt-3 text-xs flex items-center gap-1" style={{ color: C.blueGlow }}>
                      Read <ArrowUpRight size={12} />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
      <PlugitAIChat />
    </div>
  );
}

function TopNav() {
  return (
    <header className="fixed top-0 left-0 right-0 z-50"
            style={{ background: "rgba(10,15,30,0.72)", backdropFilter: "blur(14px)", borderBottom: `1px solid ${C.line}` }}>
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src={BRAND.logos.plugitai} alt="PlugitAI" className="h-10 md:h-12 w-auto" />
          <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.22em] pl-3 border-l gold-shine gold-shine-dark" style={{ borderColor: C.line }}>
            Powered by {BRAND.engineName}
          </span>
        </Link>
        <a href="tel:+12148155966"
           className="inline-flex items-center gap-2 px-4 md:px-5 h-10 md:h-11 rounded-full font-semibold text-sm"
           style={{ background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`, color: "white" }}>
          <Phone size={14} /> (214) 815-5966
        </a>
      </div>
    </header>
  );
}
